import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { WYSIWYGBlock } from '@models/wysiwyg-block';
import { GradeLevel } from '@models/grade-level';
import { AudioState, IAudioState } from '@models/audio-state';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  ITranscriptionResult,
  ITranscriptWord,
  TranscriptWord,
} from '@models/grade-unit/transcript';
import { debounceTime } from 'rxjs/operators';
import { AudioService } from '@core/services/audio.service';

@Component({
  selector: 'app-formatted-karaoke-player',
  templateUrl: './formatted-karaoke-player.component.html',
  styleUrls: ['./formatted-karaoke-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FormattedKaraokePlayerComponent implements OnInit {
  @Input() public block: WYSIWYGBlock;
  public currentTime$ = new BehaviorSubject<number>(0);
  public gradeLevel: GradeLevel;
  public transcriptionResult: ITranscriptionResult;
  public paragraph: ITranscriptWord[];
  public mappedHTML: string;

  private subscriptions: Subscription[] = [];

  private audioObj = new Audio();
  private audioState: IAudioState = new AudioState();
  public audioSrc: string = '';
  public audio$: BehaviorSubject<IAudioState> = new BehaviorSubject(
    this.audioState,
  );
  public get audio(): Observable<IAudioState> {
    return this.audio$.asObservable();
  }

  constructor(private audioService: AudioService) {}

  public ngOnInit() {
    const parser = new DOMParser();
    if (this.block) {
      this.gradeLevel = this.block.gradeLevel;
      this.audioSrc = this.block?.file?.signedUrl;
      if (this.block.originalTranscription)
        this.transcriptionResult = JSON.parse(
          this.block.originalTranscription,
        ) as ITranscriptionResult;
      if (this.block.html) {
        this.mappedHTML = this.mapTranscriptToHTML();
        const parsedHTML = parser.parseFromString(this.block.html, 'text/html');
        const originalParagraphs = [...parsedHTML.querySelectorAll('p')].map(
          (el) => {
            return el.textContent.trim().split(' ');
          },
        );
        this.paragraph = originalParagraphs?.flatMap((paragraph) => {
          return this.transcriptionResult?.items?.map((item, index, array) => {
            return new TranscriptWord({
              id: index,
              start: item.start_time,
              end: item.end_time,
            });
          });
        });
      }
    }
  }
  public loadAndPlay(event: any): void {
    event.preventDefault();
    const loadSubscription = this.audioService
      .loadStream(
        this.audio$,
        this.audioObj,
        this.audioState,
        this.currentTime$,
        this.audioSrc,
      )
      .pipe(debounceTime(10))
      .subscribe(() => this.currentTime$.next(this.audioState.currentTime));
    this.subscriptions.push(loadSubscription);
  }

  public pause(event: any): void {
    event.preventDefault();
    this.audioService.pause(this.audioObj);
  }

  public resume(event: any): void {
    event.preventDefault();
    this.audioService.resume(this.audioObj);
  }

  public restart(event: any): void {
    event.preventDefault();
    this.audioService.restart(this.audioObj);
  }

  public stop(event: any): void {
    event.preventDefault();
    this.audioService.stop();
    this.currentTime$.next(0);
  }

  private mapTranscriptToHTML() {
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.block.html, 'text/html');
    const index: { count: number } = { count: 0 };
    [...doc.childNodes].forEach((element) => this.drill(doc, element, index));
    return doc.body.innerHTML;
  }

  private drill(doc: Document, element: Node, index: { count: number }) {
    if (element.nodeType === Node.TEXT_NODE) {
      // Create span node with index
      const parent = element.parentNode;
      const punctuationRegEx = /^([?.!])/;
      if (!parent) return;
      const fragment = new DocumentFragment();
      const words = element.textContent.trim().split(' ');
      words.forEach((word, i) => {
        if (!word) return;
        const newNode = doc.createElement('span');
        const isPunctuation = punctuationRegEx.exec(word)?.length > 0;
        if (index.count > 0 && !isPunctuation) {
          newNode.textContent = ` ${word}`;
        } else {
          newNode.textContent = word;
        }
        newNode.classList.add('word');
        fragment.appendChild(newNode);
        index.count++;
      });
      parent.replaceChild(fragment, element);
      return;
    } else {
      [...element.childNodes].forEach((child) => this.drill(doc, child, index));
    }
  }
}
